import { createRouter, createWebHistory } from 'vue-router'
import GuestLayout from '@/layouts/GuestLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import LoginView from '@/views/LoginView.vue'
import TwoFactorView from '@/views/TwoFactorView.vue'
import { usePatientStore } from '@/stores/patient'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/',
            redirect: '/login',
        },
        {
            path: '/',
            component: GuestLayout,
            children: [
                {
                    path: 'login',
                    name: 'login',
                    component: LoginView,
                    meta: { requiresGuest: true },
                },
                {
                    path: 'forgot-password',
                    name: 'forgot-password',
                    component: () => import('@/views/ForgotPasswordView.vue'),
                    meta: { requiresGuest: true },
                },
                {
                    path: 'patient-reset-password',
                    name: 'patient-reset-password',
                    component: () => import('@/views/ResetPasswordView.vue'),
                    meta: { requiresGuest: true },
                },
            ],
        },
        {
            path: '/',
            component: AuthLayout,
            children: [
                {
                    path: 'two-factor',
                    name: 'two-factor',
                    component: TwoFactorView,
                },
                {
                    path: 'enable-2fa',
                    name: 'enable-2fa',
                    component: () => import('@/views/TwoFactorEnableView.vue'),
                },
                {
                    path: 'patient-confirmation',
                    name: 'patient-confirmation',
                    component: () => import('@/views/OnboardingView.vue'),
                },
                {
                    path: 'dashboard',
                    name: 'dashboard',
                    component: () => import('@/views/DashboardView.vue'),
                    meta: { requiresAuth: true },
                },
                {
                    path: 'patient-profile',
                    name: 'patient-profile',
                    component: () => import('@/views/PatientProfile.vue'),
                    meta: { requiresAuth: true },
                },
                {
                    path: 'consultation-history',
                    name: 'consultation-history',
                    component: () => import('@/views/ConsultationHistory.vue'),
                    meta: { requiresAuth: true },
                },
                {
                    path: 'setting-view',
                    name: 'setting-view',
                    component: () => import('@/views/SettingView.vue'),
                    meta: { requiresAuth: true },
                },
                {
                    path: 'consultation/:consultation_id/details',
                    name: 'consultation-details',
                    component: () => import('@/views/ConsultationDetails.vue'),
                    meta: { requiresAuth: true },
                },
                {
                    path: 'consultation/:consultation_id/referral',
                    name: 'referral',
                    component: () => import('@/views/ReferralDetails.vue'),
                    meta: { requiresAuth: true },
                },
                {
                    path: 'grab-and-go',
                    name: 'grab-and-go',
                    component: () => import('@/views/GrabAndGo.vue'),
                    meta: { requiresAuth: true },
                },
                {
                    path: 'self-care-advice/:consultation_id',
                    name: 'self-care-advice',
                    component: () => import('@/views/SelfCareAdvice.vue'),
                    meta: { requiresAuth: true },
                },
            ],
        },
    ],
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
    const patientStore = usePatientStore()

    // Check if user is authenticated
    const isAuthenticated = () => {
        return !!localStorage.getItem('patient_token') && !!patientStore.patient
    }

    // Try to load patient if token exists but patient data doesn't
    // Skip for 2FA setup routes where patient data may not be available yet
    const skipEnsurePatientRoutes = ['/enable-2fa', '/two-factor']
    if (localStorage.getItem('patient_token') && !patientStore.patient && !skipEnsurePatientRoutes.includes(to.path)) {
        try {
            await patientStore.ensurePatient()
        } catch (error) {
            // Token is invalid, clear it
            //localStorage.removeItem('patient_token')
        }
    }

    if (to.meta.requiresAuth && !isAuthenticated()) {
        // Redirect to login if authentication is required but user is not authenticated
        next('/login')
    } else if (to.meta.requiresGuest && isAuthenticated()) {
        // Redirect to dashboard if guest route but user is authenticated
        next('/dashboard')
    } else {
        next()
    }
})

export default router
